import { useEffect, useRef, useState, type RefObject } from "react";
import { useLocation, useNavigate } from "react-router";
import { useAppDispatch } from "@/app/hooks";
import { workspaceStoreSeedSelected } from "@/features/settings/activeStoreSlice";
import {
  firstConversationShopCandidate,
  workspaceConversationPath,
} from "@/features/chat/conversationShopScope";
import { desktopShellRoutes, myFilesDrawerLocation, openMyFilesDrawerSearch } from "@/features/desktop-shell/desktopShellData";
import { MerchantHomeMarketDialog } from "./MerchantHomeMarketDialog";
import { MerchantHomeShopDialog } from "./MerchantHomeShopDialog";
import { opportunityNeedsMarket } from "./merchantHomeOpportunityAction";
import type { MerchantHomeOpportunity } from "./merchantHomeSchemas";
import type { useMerchantHomeScope } from "./useMerchantHomeScope";
import type { WorkbenchAction, WorkbenchContent } from "./workbench/contentSchemas";

type Opportunity = MerchantHomeOpportunity;
type LegacyMarketRequest = { scopeKey: string; cardType: Opportunity["type"] };
type CanonicalIntentAction = Extract<WorkbenchAction, { kind: "conversation_intent" }>;
type CanonicalConversationAction = Extract<WorkbenchAction, { kind: "conversation_navigation" }>;
type CanonicalResourceAction = Extract<WorkbenchAction, { kind: "resource_navigation" }>;

export function canonicalConversationPath(action: CanonicalConversationAction, shopBindingId = "") {
  return workspaceConversationPath(action.conversation_id, shopBindingId);
}

export function canonicalResourceLocation(action: CanonicalResourceAction, search: string) {
  const params = new URLSearchParams(search);
  params.set(myFilesDrawerLocation.resourceQueryKey, action.resource_id);
  return {
    pathname: desktopShellRoutes.home.path,
    search: openMyFilesDrawerSearch(params.toString(), { preserveResource: true }),
  };
}

// 小卡保留旧接口合同；仅结构化缺市场原因转为本地范围表单，其余动作仍交原意图链。
export function useMerchantHomeOpportunityAction(
  scope: ReturnType<typeof useMerchantHomeScope> & { opportunities: { currentData?: unknown; isError: boolean } }, cards: Opportunity[], root: RefObject<HTMLElement | null>,
  onIntent: (card: Opportunity, action: Opportunity["actions"][number]) => void,
) {
  const keyFor = (market: string, category: string) => JSON.stringify([scope.scopeKey, market, category]);
  const market = scope.opportunityScope.market ?? ""; const category = scope.opportunityScope.category ?? "";
  const scopeKey = keyFor(market, category);
  const [request, setRequest] = useState<LegacyMarketRequest | null>(null);
  useEffect(() => {
    if (!request || request.scopeKey === scopeKey) return;
    // 范围可能在一帧内切走再切回；失效请求必须最终清掉，不能因 effect 清理取消后重新出现。
    queueMicrotask(() => setRequest(current => current === request ? null : current));
  }, [request, scopeKey]);
  const pendingFocus = useRef<(LegacyMarketRequest & { fromScopeKey: string }) | null>(null);
  const data = scope.opportunities.currentData; const failed = scope.opportunities.isError;
  useEffect(() => {
    const pending = pendingFocus.current; if (!pending) return;
    if (pending.scopeKey !== scopeKey) {
      // 关闭弹层可先于 Router 的范围提交；只在导航实际离开原范围后判断失效。
      if (pending.fromScopeKey !== scopeKey) pendingFocus.current = null;
      return;
    }
    if (failed) { pendingFocus.current = null; return; }
    // 新范围的来源可能晚于 URL 返回，等对应卡片重新挂载后再还焦。
    if (!data) return;
    const frame = requestAnimationFrame(() => {
      const card = root.current?.querySelector<HTMLElement>(`.merchant-home-opportunity[data-card-type="${pending.cardType}"]`);
      // 新范围也可能无动作或仅有禁用动作；卡片本身始终提供可靠的程序焦点。
      const target = card?.querySelector<HTMLElement>("button.merchant-home__opportunity-heading:not(:disabled)") ?? card
        ?? root.current?.querySelector<HTMLElement>(".merchant-home__opportunity-deck, .merchant-home-opportunity");
      if (target) { pendingFocus.current = null; target.focus({ preventScroll: true }); }
    });
    return () => cancelAnimationFrame(frame);
  }, [data, failed, root, scopeKey]);
  const perform = (id: string, actionId: string) => {
    const card = cards.find(candidate => candidate.id === id); const action = card?.actions[Number(actionId)];
    if (!card || !action?.enabled) return;
    if (opportunityNeedsMarket(card, action)) { setRequest({ scopeKey, cardType: card.type }); return; }
    onIntent(card, action);
  };
  const dialog = request?.scopeKey === scopeKey ? <MerchantHomeMarketDialog market={market} category={category} onClose={() => setRequest(null)} onChange={(nextMarket, nextCategory) => {
    pendingFocus.current = { scopeKey: keyFor(nextMarket, nextCategory), fromScopeKey: scopeKey, cardType: request.cardType };
    setRequest(null); scope.setMarket(nextMarket, nextCategory);
  }} /> : null;
  return { perform, dialog };
}

export function canonicalActionFor(cards: readonly WorkbenchContent[], contentId: string, actionId: string) {
  const index = Number(actionId);
  if (!Number.isInteger(index) || index < 0) return undefined;
  return cards.find(card => card.content_id === contentId)?.actions[index];
}

// 正式大小卡只执行服务端下发的 WorkbenchAction；不再根据卡片类型或错误文案补授权。
export function useMerchantHomeCanonicalAction(
  scope: ReturnType<typeof useMerchantHomeScope>,
  root: RefObject<HTMLElement | null>,
  onIntent: (action: CanonicalIntentAction) => void,
) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const market = scope.opportunityScope.market ?? "";
  const category = scope.opportunityScope.category ?? "";
  const keyFor = (nextMarket: string, nextCategory: string) => JSON.stringify([scope.scopeKey, nextMarket, nextCategory]);
  const scopeKey = keyFor(market, category);
  const [request, setRequest] = useState<{ scopeKey: string; focusSelector: string } | null>(null);
  const [shopRequest, setShopRequest] = useState<{ scopeKey: string; focusSelector: string } | null>(null);
  const pendingFocus = useRef<{ scopeKey: string; fromScopeKey: string; focusSelector: string } | null>(null);

  useEffect(() => {
    if (!request || request.scopeKey === scopeKey) return;
    queueMicrotask(() => setRequest(current => current === request ? null : current));
  }, [request, scopeKey]);
  useEffect(() => {
    if (!shopRequest || shopRequest.scopeKey === scopeKey) return;
    queueMicrotask(() => setShopRequest(current => current === shopRequest ? null : current));
  }, [shopRequest, scopeKey]);
  useEffect(() => {
    const pending = pendingFocus.current;
    if (!pending) return;
    if (pending.scopeKey !== scopeKey) {
      if (pending.fromScopeKey !== scopeKey) pendingFocus.current = null;
      return;
    }
    if (scope.content.query.isError) { pendingFocus.current = null; return; }
    if (!scope.content.query.currentData) return;
    const frame = requestAnimationFrame(() => {
      const card = root.current?.querySelector<HTMLElement>(pending.focusSelector);
      // 右栏触发点位于可拖拽卡片中；提交后聚焦卡片本身，避免把键盘焦点误放到已被新内容替换的旧动作位置。
      const target = root.current?.matches(".merchant-home-rail") ? card : card?.querySelector<HTMLElement>("button.merchant-home__opportunity-heading:not(:disabled)") ?? card
        ?? root.current?.querySelector<HTMLElement>(".merchant-home__opportunity-deck, .merchant-home-opportunity, [data-instance-id]");
      // 范围切换会重置正式右栏为收起；后续 inert 真卡不再是合法回焦点。
      const visibleTarget = target?.closest("[inert]") || target && !target.getClientRects().length
        ? Array.from(root.current?.querySelectorAll<HTMLElement>(".merchant-conversation-rail__toggle button:not(:disabled), .merchant-home-rail__compact-trigger:not(:disabled), [data-merchant-rail-item]:not([inert]) button:not(:disabled)") ?? []).find(element => element.getClientRects().length && !element.closest("[inert]"))
        : target;
      if (visibleTarget) { pendingFocus.current = null; visibleTarget.focus({ preventScroll: true }); }
    });
    return () => cancelAnimationFrame(frame);
  }, [root, scope.content.query.currentData, scope.content.query.isError, scopeKey]);

  const perform = (action: WorkbenchAction, focusSelector: string) => {
    if (!action.enabled) return;
    if (action.kind === "scope_navigation") {
      if (action.target === "shop") {
        if (scope.conversationId) navigate("/workspace");
        else setShopRequest({ scopeKey, focusSelector });
      }
      else setRequest({ scopeKey, focusSelector });
      return;
    }
    if (action.kind === "conversation_navigation") {
      navigate(
        canonicalConversationPath(
          action,
          firstConversationShopCandidate(
            scope.opportunityScope.shopBindingId,
            scope.overview.currentData?.resolved_scope.shop_binding_id,
          ),
        ),
      );
      return;
    }
    if (action.kind === "resource_navigation") {
      navigate(canonicalResourceLocation(action, location.search));
      return;
    }
    onIntent(action);
  };
  const performCard = (cards: readonly WorkbenchContent[], contentId: string, actionId: string) => {
    const card = cards.find(candidate => candidate.content_id === contentId);
    const action = canonicalActionFor(cards, contentId, actionId);
    if (!card || !action) return;
    perform(action, `.merchant-home-opportunity[data-card-type="${card.card_type}"]`);
  };
  const marketDialog = request?.scopeKey === scopeKey ? <MerchantHomeMarketDialog market={market} category={category} onClose={() => setRequest(null)} onChange={(nextMarket, nextCategory) => {
    pendingFocus.current = { scopeKey: keyFor(nextMarket, nextCategory), fromScopeKey: scopeKey, focusSelector: request.focusSelector };
    setRequest(null);
    scope.setMarket(nextMarket, nextCategory);
  }} /> : null;
  const shopDialog = shopRequest?.scopeKey === scopeKey ? <MerchantHomeShopDialog bindings={scope.stores.currentData?.bindings ?? []} onClose={() => setShopRequest(null)} onManage={() => navigate("/settings/stores")} onSelect={bindingId => {
    if (scope.conversationId || !scope.stores.currentData?.bindings.some(binding => binding.id === bindingId && binding.status === "active")) return;
    setShopRequest(null);
    dispatch(workspaceStoreSeedSelected({ organizationId: scope.query.organizationId, storeBindingId: bindingId }));
  }} /> : null;
  const dialog = marketDialog ?? shopDialog;
  return { perform, performCard, dialog, dialogOpen: Boolean(dialog) };
}
